import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaStethoscope, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaBookOpen,
  FaCertificate,
  FaCalendarAlt,
  FaChartLine,
  FaFileInvoice,
  FaVideo,
  FaCode
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const TopicCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  h3 {
    color: #0a4d68;
    margin-bottom: 1rem;
    font-size: 1.2rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const EMMatrix = styled.div`
  background: #f8fafc;
  border-radius: 15px;
  padding: 2rem;
  margin: 3rem 0;
  overflow-x: auto;
  
  table {
    width: 100%;
    border-collapse: collapse;
    
    th {
      background: #0a4d68;
      color: white;
      padding: 1rem;
      text-align: left;
    }
    
    td {
      padding: 1rem;
      border-bottom: 1px solid rgba(10, 77, 104, 0.1);
    }
    
    tr:last-child td {
      border-bottom: none;
    }
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const CPTEM = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const topics = [
    {
      title: "Evaluation & Management",
      items: [
        "1995 vs 1997 Documentation Guidelines",
        "History, Examination, Medical Decision Making",
        "Time-based coding",
        "Prolonged services"
      ]
    },
    {
      title: "Surgery Coding",
      items: [
        "Surgical package definition",
        "Global period determination",
        "Modifier usage",
        "Multiple procedure rules"
      ]
    },
    {
      title: "Medicine & Special Services",
      items: [
        "Immunizations and vaccines",
        "Cardiovascular services",
        "Pulmonary function testing",
        "Allergy testing and immunotherapy"
      ]
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          CPT & E/M Coding
          <span>Master Current Procedural Terminology</span>
        </HeroTitle>
        <HeroDescription>
          Comprehensive training in CPT coding including Evaluation and Management services, 
          surgical procedures, and specialty coding. [citation:7]
        </HeroDescription>
      </HeroSection>

      <Container>
       

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              This program introduces class participants to the basic principles of coding for 
              outpatient and physician services. Instruction includes beginning through advanced 
              CPT coding, covering information about anatomy, terminology, and various procedures, 
              as well as demonstrations and examples on how to code each service. [citation:7]
            </p>
            <p>
              The course will prepare participants to sit for the AAPC Certified Professional Coder 
              (CPC) exam, providing practical application of coding operative reports and evaluation 
              and management services. [citation:7]
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1581093458791-9a3c3a2c3e4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="CPT Coding"
            />
          </ImageWrapper>
        </Grid2Col>

        <SectionTitle>Course Topics</SectionTitle>
        <Grid3Col>
          {topics.map((topic, index) => (
            <TopicCard key={index}>
              <h3>{topic.title}</h3>
              <List>
                {topic.items.map((item, idx) => (
                  <ListItem key={idx}>
                    <FaCheckCircle /> {item}
                  </ListItem>
                ))}
              </List>
            </TopicCard>
          ))}
        </Grid3Col>

        <EMMatrix>
          <h3 style={{ marginBottom: '1rem' }}>E/M Code Levels Overview</h3>
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Level</th>
                <th>History</th>
                <th>Exam</th>
                <th>MDM</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>99281</td>
                <td>Level 1</td>
                <td>Problem Focused</td>
                <td>Problem Focused</td>
                <td>Straightforward</td>
              </tr>
              <tr>
                <td>99282</td>
                <td>Level 2</td>
                <td>Expanded PF</td>
                <td>Expanded PF</td>
                <td>Low</td>
              </tr>
              <tr>
                <td>99283</td>
                <td>Level 3</td>
                <td>Detailed</td>
                <td>Detailed</td>
                <td>Moderate</td>
              </tr>
              <tr>
                <td>99284</td>
                <td>Level 4</td>
                <td>Comprehensive</td>
                <td>Comprehensive</td>
                <td>Moderate</td>
              </tr>
              <tr>
                <td>99285</td>
                <td>Level 5</td>
                <td>Comprehensive</td>
                <td>Comprehensive</td>
                <td>High</td>
              </tr>
            </tbody>
          </table>
        </EMMatrix>

        <CTASection>
          <h3>Ready to Master CPT Coding?</h3>
          <p>Join our next batch and become a certified professional coder</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default CPTEM;